/**
 * Volledige interactieve product-demo — scenario's, scope, posten toggles, vergrendeling tot opslaan.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Check,
  ChevronRight,
  FileCheck,
  FileSpreadsheet,
  FileStack,
  FileText,
  FolderKanban,
  Lock,
  MessageCircle,
  Search,
  Sparkles,
  Unlock,
  Upload,
} from 'lucide-react';
import { AnimatedSection } from '../../components/AnimatedSection';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { formatEuroNl, formatQuantityNl } from '../../lib/format-nl';
import {
  type DemoKlanttype,
  type DemoScenarioId,
  type DemoScope,
  SCENARIO_LINES,
  SCENARIO_META,
  computeActiveSubtotaal,
  defaultActiveLineIds,
  klanttypeLabel,
  lineIsActive,
  loadDemoState,
  demoLineUnitPrice,
  demoRegionLabel,
  lineMatchesFilter,
  resultaatNarrative,
  saveDemoState,
  scopeLabel,
  scopeMultiplier,
  splitCostBuckets,
  type DemoPersistedState,
} from './demoModel';

type DemoTabId = 'gegevens' | 'posten' | 'resultaat' | 'rapportage';

const DEMO_TABS: { id: DemoTabId; label: string; Icon: typeof FolderKanban; step: number }[] = [
  { id: 'gegevens', label: 'Gegevens', Icon: FolderKanban, step: 1 },
  { id: 'posten', label: 'Posten', Icon: FileStack, step: 2 },
  { id: 'resultaat', label: 'Resultaat', Icon: BarChart3, step: 3 },
  { id: 'rapportage', label: 'Rapportage', Icon: FileCheck, step: 4 },
];

const TRIAL_LOGIN = '/login?returnTo=%2Fprojects%2Fnew';

const SCENARIO_ORDER: DemoScenarioId[] = ['renovatie', 'splitsing', 'transformatie'];

function TabLockNotice({ onGoPosten }: { onGoPosten: () => void }) {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-xl border border-dashed border-amber-500/40 bg-amber-500/[0.06] px-6 py-10 text-center dark:bg-amber-500/[0.08]"
      role="status"
    >
      <Lock className="mb-3 h-10 w-10 text-amber-600 dark:text-amber-400" aria-hidden />
      <p className="text-sm font-semibold text-text">Eerst posten vastleggen</p>
      <p className="mt-2 max-w-sm text-sm text-text-muted">
        In de echte app ontgrendel je <strong className="text-text">Resultaat</strong> en{' '}
        <strong className="text-text">Rapportage</strong> door op Posten op{' '}
        <strong className="text-text">Regels opslaan</strong> te klikken. Doe dat hieronder ook even in de demo.
      </p>
      <button
        type="button"
        onClick={onGoPosten}
        className="mt-5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-alt"
      >
        Naar Posten
      </button>
    </div>
  );
}

export function ProductDemo() {
  const [scenario, setScenario] = useState<DemoScenarioId>('renovatie');
  const [scope, setScope] = useState<DemoScope>(1);
  const [klant, setKlant] = useState<DemoKlanttype>('particulier');
  const [activeIds, setActiveIds] = useState<Set<string>>(() => defaultActiveLineIds('renovatie'));
  const [rulesSaved, setRulesSaved] = useState(false);
  const [tab, setTab] = useState<DemoTabId>('gegevens');
  const [drawingChecked, setDrawingChecked] = useState(false);
  const [docLibraryChecked, setDocLibraryChecked] = useState(true);
  const [postenFilter, setPostenFilter] = useState('');
  const [savePulse, setSavePulse] = useState(false);
  const [exportHint, setExportHint] = useState<string | null>(null);

  useEffect(() => {
    const p = loadDemoState();
    if (!p) return;
    if (p.scenario && SCENARIO_ORDER.includes(p.scenario)) setScenario(p.scenario);
    if (p.scope !== undefined && [0, 1, 2].includes(p.scope)) setScope(p.scope as DemoScope);
    if (p.klant) setKlant(p.klant);
    if (p.activeIds?.length) setActiveIds(new Set(p.activeIds));
    if (typeof p.rulesSaved === 'boolean') setRulesSaved(p.rulesSaved);
    if (p.tab) setTab(p.tab);
    if (typeof p.drawingChecked === 'boolean') setDrawingChecked(p.drawingChecked);
    if (typeof p.docLibraryChecked === 'boolean') setDocLibraryChecked(p.docLibraryChecked);
  }, []);

  const persist = useCallback(() => {
    const state: DemoPersistedState = {
      scenario,
      scope,
      klant,
      activeIds: [...activeIds],
      rulesSaved,
      tab,
      drawingChecked,
      docLibraryChecked,
    };
    saveDemoState(state);
  }, [scenario, scope, klant, activeIds, rulesSaved, tab, drawingChecked, docLibraryChecked]);

  useEffect(() => {
    persist();
  }, [persist]);

  const meta = SCENARIO_META[scenario];
  const lines = SCENARIO_LINES[scenario];

  const filteredLines = useMemo(() => {
    const q = postenFilter.trim().toLowerCase();
    return lines.filter((l) => lineMatchesFilter(l, q));
  }, [lines, postenFilter]);

  const activeLineCount = useMemo(
    () => lines.filter((l) => lineIsActive(l, activeIds)).length,
    [lines, activeIds]
  );

  const modelTotal = useMemo(
    () => (rulesSaved ? computeActiveSubtotaal(scenario, scope, activeIds) : null),
    [rulesSaved, scenario, scope, activeIds]
  );

  const buckets = useMemo(
    () => (modelTotal != null ? splitCostBuckets(modelTotal) : null),
    [modelTotal]
  );

  const narrative = useMemo(
    () =>
      rulesSaved && modelTotal != null
        ? resultaatNarrative(scenario, klant, activeLineCount, demoRegionLabel(scenario))
        : '',
    [rulesSaved, modelTotal, scenario, klant, activeLineCount]
  );

  const barPercents = useMemo(() => {
    if (!buckets || !modelTotal) return null;
    const m = (buckets.materials / modelTotal) * 100;
    const l = (buckets.labor / modelTotal) * 100;
    const e = (buckets.equipment / modelTotal) * 100;
    return { m, l, e };
  }, [buckets, modelTotal]);

  function handleScenarioChange(next: DemoScenarioId) {
    setScenario(next);
    setActiveIds(defaultActiveLineIds(next));
    setRulesSaved(false);
    setPostenFilter('');
  }

  function toggleLine(id: string) {
    setActiveIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setRulesSaved(false);
  }

  function handleSaveRules() {
    setRulesSaved(true);
    setSavePulse(true);
    window.setTimeout(() => setSavePulse(false), 900);
    setTab('resultaat');
  }

  function showExportDemo(kind: string) {
    setExportHint(kind);
    window.setTimeout(() => setExportHint(null), 3200);
  }

  const regionLabel = meta.dossierNaam.includes('Utrecht')
    ? '3511 — Midden-Nederland'
    : meta.dossierNaam.includes('Haarlem')
      ? '2011 — Randstad'
      : '5611 — Zuidoost-Brabant';

  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/demo"
        title="Interactieve product-demo — dossier in vier stappen"
        description="Probeer CalqTool™ live zonder account: kies een scenario, stel je dossier in, activeer posten en zie modeltotalen. Daarna start je proefperiode voor echte bibliotheek en exports."
      />
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Demo' }]} />

      <section className="py-8 sm:py-12">
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
            Interactief · geen account · fictieve bedragen
          </p>
          <h1 className="text-3xl font-bold text-text sm:text-4xl">Live product-demo</h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-text-muted">
            Kies een <strong className="font-semibold text-text">scenario</strong>, pas <strong className="font-semibold text-text">omvang</strong> en{' '}
            <strong className="font-semibold text-text">posten</strong> aan, sla op en bekijk hoe{' '}
            <strong className="font-semibold text-text">Resultaat</strong> en <strong className="font-semibold text-text">Rapportage</strong> in de app
            aansluiten — hetzelfde viertabs-ritme als in je echte dossier.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to={TRIAL_LOGIN}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition hover:bg-primary-alt"
            >
              Start proefperiode
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <Link
              to="/prijzen"
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-card px-5 py-2.5 text-sm font-medium text-text hover:bg-surface"
            >
              Pakketten
            </Link>
          </div>
        </div>
      </section>

      {/* Scenario + scope */}
      <AnimatedSection className="mx-auto max-w-4xl px-0 sm:px-0">
        <div className="mb-6 grid gap-4 sm:grid-cols-3" data-animate>
          {SCENARIO_ORDER.map((id) => {
            const m = SCENARIO_META[id];
            const sel = scenario === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => handleScenarioChange(id)}
                className={`rounded-xl border p-4 text-left transition ${
                  sel
                    ? 'border-primary bg-primary/[0.08] shadow-sm ring-2 ring-primary/25'
                    : 'border-border bg-surface-card hover:border-primary/40 hover:bg-surface'
                }`}
              >
                <span className={`text-sm font-semibold ${sel ? 'text-primary' : 'text-text'}`}>{m.label}</span>
                <p className="mt-2 text-xs leading-relaxed text-text-muted">{m.teaser}</p>
              </button>
            );
          })}
        </div>

        <div
          className="mb-8 rounded-xl border border-border bg-surface-card px-4 py-4 sm:px-5"
          data-animate
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Omvang van het voorbeeld</p>
          <p className="mt-1 text-sm text-text-muted">
            Verschuift alle bedragen — puur om het effect van schaal op een modelbegroting te laten zien.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <input
              type="range"
              min={0}
              max={2}
              step={1}
              value={scope}
              onChange={(e) => {
                setScope(Number(e.target.value) as DemoScope);
                setRulesSaved(false);
              }}
              className="h-2 w-full max-w-xs cursor-pointer accent-primary"
              aria-valuetext={scopeLabel(scope)}
            />
            <span className="text-sm font-medium text-text">{scopeLabel(scope)}</span>
          </div>
        </div>
      </AnimatedSection>

      {/* Stepper */}
      <div className="mx-auto mb-4 max-w-4xl">
        <ol className="flex flex-wrap items-center justify-center gap-1 sm:gap-2" aria-label="Demo-stappen">
          {DEMO_TABS.map(({ id, label, step }, idx) => {
            const active = tab === id;
            const done =
              (step === 1 && tab !== 'gegevens') ||
              (step === 2 && (tab === 'resultaat' || tab === 'rapportage')) ||
              (step === 3 && tab === 'rapportage') ||
              (step === 4 && false);
            return (
              <li key={id} className="flex items-center">
                <button
                  type="button"
                  onClick={() => setTab(id)}
                  className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition sm:text-sm ${
                    active
                      ? 'bg-primary text-white shadow-sm'
                      : done
                        ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200'
                        : 'bg-surface text-text-muted hover:bg-surface-card hover:text-text'
                  }`}
                >
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
                      active ? 'bg-white/20' : done ? 'bg-emerald-500/25' : 'bg-border/80'
                    }`}
                  >
                    {done ? <Check className="h-3.5 w-3.5" aria-hidden /> : step}
                  </span>
                  <span className="hidden sm:inline">{label}</span>
                </button>
                {idx < DEMO_TABS.length - 1 && (
                  <ChevronRight className="mx-0.5 h-4 w-4 shrink-0 text-text-muted/50 hidden sm:block" aria-hidden />
                )}
              </li>
            );
          })}
        </ol>
      </div>

      <AnimatedSection>
        <div className="mx-auto max-w-4xl">
          <div
            className="overflow-hidden rounded-2xl border border-border bg-surface-card shadow-md"
            role="region"
            aria-label="Interactief voorbeelddossier"
          >
            <div className="flex flex-col gap-2 border-b border-border bg-surface px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-4">
              <div>
                <p className="text-xs font-medium text-text-muted sm:text-sm">
                  Voorbeelddossier <span className="text-text">(demo)</span>
                </p>
                <p className="font-semibold text-text">{meta.dossierNaam}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {drawingChecked && (
                  <span className="rounded-full bg-sky/20 px-2 py-0.5 text-[11px] font-medium text-navy dark:text-sky">
                    Tekening in bibliotheek
                  </span>
                )}
                {rulesSaved && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-800 dark:text-emerald-200">
                    <Unlock className="h-3 w-3" aria-hidden />
                    Posten opgeslagen
                  </span>
                )}
              </div>
            </div>

            <div
              className="flex flex-wrap gap-1 border-b border-border bg-surface-card px-2 py-2 sm:gap-0 sm:px-3"
              role="tablist"
              aria-label="Dossier-tabs demo"
            >
              {DEMO_TABS.map(({ id, label, Icon }) => {
                const selected = tab === id;
                const locked = (id === 'resultaat' || id === 'rapportage') && !rulesSaved;
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    aria-controls={`demo-panel-${id}`}
                    id={`demo-tab-${id}`}
                    onClick={() => setTab(id)}
                    className={`relative flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs font-medium transition sm:flex-none sm:px-3 sm:text-sm ${
                      selected
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-text-muted hover:bg-surface hover:text-text'
                    } ${locked ? 'opacity-90' : ''}`}
                  >
                    {locked && (
                      <Lock className="absolute right-1 top-1 h-3 w-3 text-amber-600 opacity-80 sm:right-2 sm:top-2 dark:text-amber-400" aria-hidden />
                    )}
                    <Icon className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="truncate">{label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-4 sm:p-6">
              {tab === 'gegevens' && (
                <div id="demo-panel-gegevens" role="tabpanel" aria-labelledby="demo-tab-gegevens" className="space-y-6">
                  <p className="text-sm text-text-muted">
                    Stel de <strong className="text-text">context</strong> van het dossier in — in de app sluiten tips en
                    detailniveau hierop aan. Op <strong className="text-text">Posten</strong> zie je een uitgebreide
                    mock van het prijsboek: hoofdgroep, subgroep, library-referentie en variant (zoals bij echte
                    catalogusregels).
                  </p>

                  <div className="rounded-lg border border-primary/20 bg-primary/[0.05] px-4 py-3 text-sm text-text-muted dark:bg-primary/[0.08]">
                    <strong className="text-text">Bibliotheek (echte app):</strong> je zoekt per hoofdgroep en subgroep,
                    kiest een <strong className="text-text">systeem + variant</strong> uit de NL-prijsbibliotheek, vult
                    hoeveelheid en bouwcluster in — elke regel blijft reproduceerbaar. Deze demo gebruikt{' '}
                    <strong className="text-text">fictieve DEMO-NL-* codes</strong> en geen live API.
                  </div>

                  <div>
                    <label htmlFor="demo-klant" className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Klanttype
                    </label>
                    <select
                      id="demo-klant"
                      value={klant}
                      onChange={(e) => setKlant(e.target.value as DemoKlanttype)}
                      className="mt-1.5 w-full max-w-md rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text"
                    >
                      <option value="particulier">{klanttypeLabel('particulier')}</option>
                      <option value="zakelijk">{klanttypeLabel('zakelijk')}</option>
                      <option value="corporatie">{klanttypeLabel('corporatie')}</option>
                    </select>
                  </div>

                  <dl className="grid gap-3 text-sm sm:grid-cols-2">
                    <div className="rounded-lg border border-border bg-surface p-3">
                      <dt className="text-xs font-semibold uppercase tracking-wide text-text-muted">Projecttype</dt>
                      <dd className="mt-1 font-medium text-text">{meta.projecttype}</dd>
                    </div>
                    <div className="rounded-lg border border-border bg-surface p-3">
                      <dt className="text-xs font-semibold uppercase tracking-wide text-text-muted">Regio / postcode</dt>
                      <dd className="mt-1 font-medium text-text">{regionLabel}</dd>
                    </div>
                    <div className="rounded-lg border border-border bg-surface p-3">
                      <dt className="text-xs font-semibold uppercase tracking-wide text-text-muted">Indexjaar (model)</dt>
                      <dd className="mt-1 font-medium text-text">2026</dd>
                    </div>
                    <div className="rounded-lg border border-border bg-surface p-3">
                      <dt className="text-xs font-semibold uppercase tracking-wide text-text-muted">Omvang-demo</dt>
                      <dd className="mt-1 font-medium text-text">{scopeLabel(scope)}</dd>
                    </div>
                  </dl>

                  <div className="rounded-xl border border-border bg-surface/80 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Documentenbibliotheek</p>
                    <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-transparent p-2 hover:bg-surface-card">
                      <input
                        type="checkbox"
                        checked={docLibraryChecked}
                        onChange={(e) => setDocLibraryChecked(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-border text-primary"
                      />
                      <span className="text-sm text-text-muted">
                        <strong className="text-text">Plattegrond + bestek (PDF)</strong> — in de app koppel je documenten
                        aan het dossier voor analyse en referentie.
                      </span>
                    </label>
                    <label className="mt-2 flex cursor-pointer items-start gap-3 rounded-lg border border-transparent p-2 hover:bg-surface-card">
                      <input
                        type="checkbox"
                        checked={drawingChecked}
                        onChange={(e) => setDrawingChecked(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-border text-primary"
                      />
                      <span className="text-sm text-text-muted">
                        <strong className="text-text">Tekening beschikbaar</strong> — optionele stap: analyse → voorstel →
                        jij keurt goed vóór import naar posten.
                      </span>
                    </label>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-surface-card/50 px-4 py-3">
                    <div className="flex items-center gap-2 text-sm text-text-muted">
                      <Upload className="h-5 w-5 shrink-0 text-primary" aria-hidden />
                      <span>Uploadzone verschijnt in de app op dit scherm.</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setTab('posten')}
                      className="inline-flex items-center gap-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-alt"
                    >
                      Verder naar Posten
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                </div>
              )}

              {tab === 'posten' && (
                <div id="demo-panel-posten" role="tabpanel" aria-labelledby="demo-tab-posten" className="space-y-4">
                  <p className="text-sm text-text-muted">
                    Elke rij toont <strong className="text-text">hoofdgroep → subgroep</strong>, een{' '}
                    <strong className="text-text">library-referentie</strong> (demo) en de gekozen{' '}
                    <strong className="text-text">variant</strong>. Vink optionele pakketten aan of uit. Daarna{' '}
                    <strong className="text-text">Regels opslaan</strong> — zo ontgrendel je Resultaat en Rapportage, net
                    als in de app.
                  </p>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" aria-hidden />
                    <input
                      type="search"
                      placeholder="Zoek op code, discipline, hoofdgroep, cluster of omschrijving…"
                      value={postenFilter}
                      onChange={(e) => setPostenFilter(e.target.value)}
                      className="w-full rounded-lg border border-border bg-surface py-2.5 pl-10 pr-3 text-sm"
                      aria-label="Filter posten"
                    />
                  </div>

                  <p className="text-xs text-text-muted">
                    {lines.length} modelregels in dit scenario ({lines.filter((l) => l.optional).length} optioneel). Horizontaal
                    scrollen op smalle schermen.
                  </p>

                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full min-w-[920px] text-left text-sm">
                      <thead className="bg-surface text-[10px] font-semibold uppercase tracking-wide text-text-muted sm:text-xs">
                        <tr>
                          <th className="sticky left-0 z-10 w-10 bg-surface px-1 py-2 sm:px-2"> </th>
                          <th className="px-2 py-2 font-mono">Library-ref</th>
                          <th className="px-2 py-2">Disc.</th>
                          <th className="min-w-[200px] px-2 py-2">Hoofdgroep / post</th>
                          <th className="px-2 py-2 text-right">Eenh.</th>
                          <th className="px-2 py-2 text-right">Aant.</th>
                          <th className="hidden px-2 py-2 text-right lg:table-cell">€/eenh.</th>
                          <th className="sticky right-0 z-10 bg-surface px-2 py-2 text-right shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)] dark:shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.25)]">
                            Totaal
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {filteredLines.map((line) => {
                          const on = lineIsActive(line, activeIds);
                          const mult = scopeMultiplier(scope);
                          const displaySub =
                            line.optional && !on
                              ? '—'
                              : formatEuroNl(Math.round(line.basisSubtotaal * mult));
                          const unitPrice =
                            line.optional && !on ? null : formatEuroNl(Math.round(demoLineUnitPrice(line) * mult));
                          return (
                            <tr
                              key={line.id}
                              className={
                                on || !line.optional ? 'bg-transparent' : 'bg-surface/50 opacity-[0.72]'
                              }
                            >
                              <td className="sticky left-0 z-10 bg-surface-card px-1 py-2.5 sm:bg-surface sm:px-2">
                                {line.optional ? (
                                  <input
                                    type="checkbox"
                                    checked={on}
                                    onChange={() => toggleLine(line.id)}
                                    aria-label={`${line.omschrijving} meenemen`}
                                    className="h-4 w-4 rounded border-border text-primary"
                                  />
                                ) : (
                                  <span
                                    className="inline-flex h-4 w-4 items-center justify-center text-emerald-600"
                                    title="Verplicht in dit scenario"
                                  >
                                    <Check className="h-4 w-4" aria-hidden />
                                  </span>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-2 py-2.5 font-mono text-[10px] text-text-muted sm:text-xs">
                                {line.libraryRef}
                              </td>
                              <td className="whitespace-nowrap px-2 py-2.5 text-xs text-text-muted">{line.discipline}</td>
                              <td className="min-w-[200px] px-2 py-2.5">
                                <span className="text-[11px] font-semibold text-primary">{line.cluster}</span>
                                <span className="block text-[11px] text-text-muted">
                                  {line.hoofdgroep} · {line.subgroep}
                                </span>
                                <span className="mt-0.5 block text-text">{line.omschrijving}</span>
                                <span className="mt-1 inline-block rounded bg-surface px-1.5 py-0.5 text-[10px] font-medium text-text-muted ring-1 ring-border">
                                  Variant: {line.variantLabel}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-2 py-2.5 text-right text-text-muted">{line.eenheid}</td>
                              <td className="whitespace-nowrap px-2 py-2.5 text-right font-medium text-text">
                                {formatQuantityNl(line.aantal)}
                              </td>
                              <td className="hidden whitespace-nowrap px-2 py-2.5 text-right text-text-muted lg:table-cell">
                                {unitPrice ?? '—'}
                              </td>
                              <td className="sticky right-0 z-10 whitespace-nowrap bg-surface-card px-2 py-2.5 text-right font-semibold text-text shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)] dark:bg-surface dark:shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.25)]">
                                {line.optional && !on ? '—' : displaySub}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {filteredLines.length === 0 && (
                      <p className="px-3 py-6 text-center text-sm text-text-muted">Geen regels voor deze filter.</p>
                    )}
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-text-muted">
                      * €/eenh. is afgeleid uit modelregel ÷ aantal (afgerond). Bedragen illustratief (
                      {scopeLabel(scope)}). Geen verbinding met de live catalogus-API.
                    </p>
                    <button
                      type="button"
                      onClick={handleSaveRules}
                      className={`inline-flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium text-white transition sm:shrink-0 ${
                        savePulse ? 'scale-[1.02] bg-emerald-600' : 'bg-primary hover:bg-primary-alt'
                      }`}
                    >
                      {savePulse ? (
                        <>
                          <Check className="mr-2 h-4 w-4" aria-hidden />
                          Opgeslagen
                        </>
                      ) : (
                        'Regels opslaan (demo)'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {tab === 'resultaat' && (
                <div id="demo-panel-resultaat" role="tabpanel" aria-labelledby="demo-tab-resultaat">
                  {!rulesSaved || !modelTotal || !buckets || !barPercents ? (
                    <TabLockNotice onGoPosten={() => setTab('posten')} />
                  ) : (
                    <div className="space-y-6">
                      <p className="text-sm text-text-muted">{narrative}</p>

                      <div className="rounded-xl border border-primary/25 bg-primary/[0.06] p-4 sm:p-6 dark:bg-primary/[0.09]">
                        <p className="text-sm font-medium text-text-muted">Modeltotaal excl. btw</p>
                        <p className="mt-1 text-3xl font-bold tracking-tight text-primary sm:text-4xl">
                          {formatEuroNl(modelTotal)}
                        </p>

                        <div className="mt-6 flex h-3 w-full overflow-hidden rounded-full bg-surface">
                          <div
                            className="h-full bg-primary transition-all duration-500"
                            style={{ width: `${barPercents.m}%` }}
                            title="Materialen"
                          />
                          <div
                            className="h-full bg-navy-light transition-all duration-500"
                            style={{ width: `${barPercents.l}%` }}
                            title="Arbeid"
                          />
                          <div
                            className="h-full bg-text-muted/35 transition-all duration-500"
                            style={{ width: `${barPercents.e}%` }}
                            title="Materieel / overig"
                          />
                        </div>
                        <ul className="mt-4 space-y-2 text-sm">
                          <li className="flex justify-between border-b border-border/60 pb-2">
                            <span className="text-text-muted">Materialen</span>
                            <span className="font-semibold text-text">{formatEuroNl(buckets.materials)}</span>
                          </li>
                          <li className="flex justify-between border-b border-border/60 pb-2">
                            <span className="text-text-muted">Arbeid</span>
                            <span className="font-semibold text-text">{formatEuroNl(buckets.labor)}</span>
                          </li>
                          <li className="flex justify-between">
                            <span className="text-text-muted">Materieel / overig</span>
                            <span className="font-semibold text-text">{formatEuroNl(buckets.equipment)}</span>
                          </li>
                        </ul>
                      </div>

                      <div className="rounded-lg border border-border bg-surface p-4 text-sm text-text-muted">
                        <strong className="text-text">Zo werkt het in de app:</strong> elke euro is terug te leiden naar
                        library-regels en varianten. Hier tonen we alleen een modelverdeling — in de proefperiode zie je
                        echte doorrekening per post.
                      </div>

                      <button
                        type="button"
                        onClick={() => setTab('rapportage')}
                        className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-card px-4 py-2 text-sm font-medium text-text hover:border-primary hover:text-primary"
                      >
                        Verder naar Rapportage
                        <ArrowRight className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {tab === 'rapportage' && (
                <div id="demo-panel-rapportage" role="tabpanel" aria-labelledby="demo-tab-rapportage">
                  {!rulesSaved ? (
                    <TabLockNotice onGoPosten={() => setTab('posten')} />
                  ) : (
                    <div className="space-y-6">
                      <p className="text-sm text-text-muted">
                        Dezelfde totalen als op Resultaat — hier kies je <strong className="text-text">calculatie-export</strong>{' '}
                        of <strong className="text-text">offerte</strong>. Klik om te zien wat er in de app gebeurt.
                      </p>

                      {exportHint && (
                        <div
                          className="rounded-lg border border-sky/40 bg-sky/15 px-4 py-3 text-sm text-navy dark:text-sky"
                          role="status"
                        >
                          <strong className="text-text">In de echte app:</strong> {exportHint}
                        </div>
                      )}

                      <div className="grid gap-6 lg:grid-cols-2">
                        <div className="rounded-xl border border-border bg-surface p-4">
                          <h3 className="flex items-center gap-2 text-sm font-semibold text-text">
                            <FileSpreadsheet className="h-4 w-4 text-primary" aria-hidden />
                            Calculatie-export
                          </h3>
                          <p className="mt-1 text-xs text-text-muted">Voor interne uitwerking — instelbaar per tenant.</p>
                          <div className="mt-4 flex flex-col gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                showExportDemo('download je Calculatie CSV (UTF-8) met de kolommen die je in Exportinstellingen hebt gekozen.')
                              }
                              className="flex items-center justify-center gap-2 rounded-lg border border-border bg-surface-card px-3 py-2.5 text-sm font-medium hover:border-primary"
                            >
                              <FileText className="h-4 w-4" aria-hidden />
                              Calculatie CSV
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                showExportDemo('genereer je Calculatie Excel (met BOM) voor je werkvoorbereiding.')
                              }
                              className="flex items-center justify-center gap-2 rounded-lg border border-border bg-surface-card px-3 py-2.5 text-sm font-medium hover:border-primary"
                            >
                              <FileSpreadsheet className="h-4 w-4" aria-hidden />
                              Calculatie Excel
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                showExportDemo('maak je Begroting-PDF met kostenopbouw volgens je exportprofiel.')
                              }
                              className="flex items-center justify-center gap-2 rounded-lg border border-border bg-surface-card px-3 py-2.5 text-sm font-medium hover:border-primary"
                            >
                              <FileCheck className="h-4 w-4" aria-hidden />
                              Begroting-PDF
                            </button>
                          </div>
                        </div>

                        <div className="rounded-xl border border-border bg-surface p-4">
                          <h3 className="flex items-center gap-2 text-sm font-semibold text-text">
                            <FileCheck className="h-4 w-4 text-primary" aria-hidden />
                            Offerte
                          </h3>
                          <p className="mt-1 text-xs text-text-muted">Zelfde cijfers, klantgerichte presentatie.</p>
                          <div className="mt-4 flex flex-col gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                showExportDemo('open je offerte-preview in de app; daarna Print/PDF via de browser.')
                              }
                              className="flex items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2.5 text-sm font-medium text-white hover:bg-primary-alt"
                            >
                              Offerte genereren
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                showExportDemo('Offerte-PDF (server) staat in Exportinstellingen — zelfde bron als je posten.')
                              }
                              className="flex items-center justify-center gap-2 rounded-lg border border-border bg-surface-card px-3 py-2.5 text-sm font-medium hover:border-primary"
                            >
                              Offerte-PDF (uitleg)
                            </button>
                          </div>
                        </div>
                      </div>

                      <p className="text-center text-xs text-text-muted">
                        Nog geen account?{' '}
                        <Link to={TRIAL_LOGIN} className="font-medium text-primary hover:underline">
                          Start proefperiode
                        </Link>{' '}
                        om échte bestanden te downloaden.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </AnimatedSection>

      <section className="mx-auto mt-12 max-w-3xl rounded-2xl border border-border bg-surface-card px-5 py-8 text-center sm:px-8">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/15">
          <MessageCircle className="h-6 w-6 text-primary" aria-hidden />
        </div>
        <h2 className="text-xl font-bold text-text sm:text-2xl">Vragen tijdens de demo?</h2>
        <p className="mx-auto mt-2 max-w-lg text-sm text-text-muted">
          Gebruik het <strong className="text-text">chat-icoon rechtsonder</strong> — publieke eerste hulp, zonder jouw
          dossierdata.
        </p>
        <p className="mt-4 text-xs text-text-muted">
          <Link to="/login?returnTo=%2Fonboarding" className="font-medium text-primary hover:underline">
            Introductie na inloggen
          </Link>
          {' · '}
          <Link to="/help/export" className="font-medium text-primary hover:underline">
            Export &amp; offerte
          </Link>
        </p>
      </section>

      <section className="mx-auto mt-10 max-w-3xl border-t border-border pt-10 text-center">
        <h2 className="text-lg font-semibold text-text">Klaar voor de echte bibliotheek?</h2>
        <p className="mt-2 text-sm text-text-muted">7 dagen proefperiode — zelfde app, echte posten en exports.</p>
        <Link
          to={TRIAL_LOGIN}
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-white hover:bg-primary-alt"
        >
          Account aanmaken of inloggen
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </section>
    </div>
  );
}
